import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { submitLeadToFollowUpBoss, type LeadData } from '../utils/crmIntegration';
import { trackModalOpen, trackModalClose, trackButtonClick } from '../utils/gtmTracking';

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Track modal open
  React.useEffect(() => {
    if (isOpen) {
      trackModalOpen('recruiter_contact_modal', 'speak_to_recruiter_button');
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    trackButtonClick('Request Call Back', 'recruiter_contact_modal', 'form_submission');
    
    try {
      // Prepare lead data for Follow Up Boss
      const leadData: LeadData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim() || `Recruiter contact request from ${formData.firstName} ${formData.lastName}. Requested callback to discuss KILOTERRA career opportunities.`,
        source: 'Careers - Speak to Recruiter', // Added "Careers" prefix
        leadType: 'recruiter_contact',
        timestamp: new Date().toISOString()
      };

      // Submit to Follow Up Boss
      console.log('🚀 Submitting recruiter contact to Follow Up Boss...');
      const success = await submitLeadToFollowUpBoss(leadData);
      
      console.log('✅ Recruiter contact submitted successfully:', { 
        contact: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        fubSuccess: success,
        redirecting: 'to thank-you page'
      });
      
      // Close modal and redirect to thank you page
      onClose();
      navigate('/thank-you?type=recruiter_contact&source=speak_to_recruiter');

    } catch (error) {
      console.error('❌ Recruiter contact submission error:', error);
      // Still redirect to thank you page even if there's an error
      onClose();
      navigate('/thank-you?type=recruiter_contact&source=speak_to_recruiter');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      trackModalClose('recruiter_contact_modal', isSubmitted);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      setIsSubmitted(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white w-full max-w-md transform transition-all duration-300 scale-100 opacity-100 shadow-2xl">
          {/* Close Button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X className="h-6 w-6" />
          </button>

          {!isSubmitted ? (
            <>
              {/* Header */}
              <div className="bg-kiloterra-dark px-6 py-8 text-center">
                <div className="flex items-center justify-center mb-4">
                  <img 
                    src="/kiloterra_logo_stacked_simple_grey.png" 
                    alt="KILOTERRA Logo" 
                    className="h-8 w-auto brightness-0 invert"
                  />
                </div>
                <h2 className="text-2xl font-bold text-white uppercase">
                  Speak to a Recruiter
                </h2>
                <p className="text-gray-200 mt-2">
                  Still have questions? Let's connect and discuss your future with KILOTERRA. Please fill out the form below. We'll never share your information.
                </p>
              </div>

              {/* Form Content */}
              <div className="px-6 py-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                        First Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="First name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                        Last Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                      How can we help?
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        disabled={isSubmitting}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Let us know what questions you have so we can be prepared to help you."
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-kiloterra-yellow text-kiloterra-dark py-4 font-bold text-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:hover:bg-kiloterra-yellow disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-wide"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Call Back'}
                  </button>
                </form>
              </div>
            </>
          ) : (
            <>
              {/* Thank You Header - Logo with Professional Greeting */}
              <div className="bg-kiloterra-dark px-6 py-8 text-center">
                <div className="flex items-center justify-center mb-4">
                  <img 
                    src="/kiloterra_logo_stacked_simple_grey.png" 
                    alt="KILOTERRA Logo" 
                    className="h-8 w-auto brightness-0 invert"
                  />
                </div>
                <h2 className="text-2xl font-bold text-white uppercase mb-2">
                  Thank You for Your Interest
                </h2>
                <p className="text-gray-200">
                  We appreciate you taking the time to connect with KILOTERRA
                </p>
              </div>

              {/* Thank You Content - Updated Message */}
              <div className="px-6 py-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-kiloterra-dark mb-2 uppercase">
                    Request Received
                  </h3>
                  <p className="text-gray-600">
                    We've received your information and a member of our team will reach out to schedule a call, typically within 3-5 business days.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModal;